import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { opportunityPaths, programmes } from "../../data/content.js";
import { Rise } from "./shared.jsx";
import "./join.css";

const FILTERS = [
  ["all", "All"],
  ["programmes", "Programmes"],
  ["learn", "Learn on your own"],
  ["contribute", "Contribute to GRID"],
  ["organisations", "For organisations"],
];

const program = (slug) => programmes.find((item) => item.slug === slug);
const path = (slug) => opportunityPaths.find((item) => item.slug === slug);

/*
  Status kinds drive the pill style only:
  open = usable now, enquiry = email us, watch = check the organiser, none = no confirmed listing.
*/
const ROWS = [
  {
    id: "upcoming",
    type: "programmes",
    title: "Upcoming GRID events",
    by: "Project GRID",
    note: "Confirmed dates, formats and eligibility appear here when organisers publish them.",
    eligibility: "Set per event",
    cost: "Set per event",
    status: ["none", "No confirmed application link yet"],
    to: "/programmes/upcoming",
  },
  {
    id: "aurora",
    type: "programmes",
    title: "Aurora",
    by: "A Project GRID programme",
    note: program("aurora").focus,
    eligibility: program("aurora").audience,
    cost: "See the official site",
    status: ["watch", "Watch the official site for the next call"],
    href: program("aurora").externalUrl,
  },
  {
    id: "aaroh",
    type: "programmes",
    title: "AAROH",
    by: "GRID event with Tachyon India and CiPD, IIIT Delhi",
    note: program("aaroh").focus,
    eligibility: `Last edition: ${program("aaroh").audience}`,
    cost: "See the official site",
    status: ["watch", "Recap published. Check the official site"],
    href: program("aaroh").externalUrl,
  },
  {
    id: "metamorphosis",
    type: "programmes",
    title: "Metamorphosis",
    by: "Led by Tale of Humankind Foundation and IIT Delhi. GRID was outreach partner",
    note: "Outreach and student participation.",
    eligibility: program("metamorphosis").audience,
    cost: "See the organiser",
    status: ["watch", "Check with the organiser"],
    href: program("metamorphosis").externalUrl,
  },
  {
    id: "guides",
    type: "learn",
    title: "Project guides",
    by: "Project GRID",
    note: "Five step-by-step guides, from finding a problem to pitching your project.",
    eligibility: "Any student",
    cost: "Free to read",
    status: ["open", "Open any time"],
    to: "/guides",
  },
  {
    id: "problems",
    type: "learn",
    title: "Problems to investigate",
    by: "Project GRID",
    note: "Five areas to investigate: education, accessibility, climate, health and civic tech.",
    eligibility: "Any student",
    cost: "Free to read",
    status: ["open", "Open any time"],
    to: "/problems",
  },
  {
    id: "team",
    type: "contribute",
    title: path("team").title,
    by: "Project GRID",
    note: path("team").promise,
    eligibility: "Not listed. Tell us what you can do",
    cost: "Not stated",
    status: ["enquiry", "Enquiries by email. Not an offer of a role"],
    to: "/student-opportunities/team",
  },
  {
    id: "internships",
    type: "contribute",
    title: path("internships").title,
    by: "Project GRID",
    note: path("internships").promise,
    eligibility: "Not yet listed",
    cost: "Compensation not confirmed",
    status: ["none", "No confirmed listing. You can ask"],
    to: "/student-opportunities/internships",
  },
  {
    id: "organisations",
    type: "organisations",
    title: path("organisations").title,
    by: "For schools, NGOs, institutions and companies",
    note: path("organisations").promise,
    eligibility: "Organisations",
    cost: "Agreed per collaboration",
    status: ["enquiry", "Enquiries by email"],
    to: "/student-opportunities/organisations",
  },
];

function RowLink({ row, children }) {
  if (row.to) return <Link className="jn-dir__link" to={row.to}>{children}</Link>;
  return <a className="jn-dir__link" href={row.href} target="_blank" rel="noreferrer">{children}</a>;
}

export default function OpportunitiesPage() {
  const reduce = useReducedMotion();
  const [filter, setFilter] = useState("all");
  const rows = ROWS.filter((row) => filter === "all" || row.type === filter);
  const count = (key) => (key === "all" ? ROWS.length : ROWS.filter((row) => row.type === key).length);

  return (
    <PageFrame className="rd-page jn-page jn-op">
      <Seo
        title="Ways to Connect with Project GRID"
        path="/student-opportunities"
        description="Connect with Project GRID as an organisation, join the team or register interest in an internship."
      />
      <main>
        <header className="jn-wrap jn-op-hero">
          <Rise as="p" className="jn-mono" delay={0.05}>Free opportunities</Rise>
          <MaskWords as="h1" className="jn-h1 jn-op-hero__title" text="Ways in, with the details up front." onMount delay={0.1} />
          <Rise as="p" className="jn-lede" delay={0.4}>
            Programmes, practical resources and ways to contribute to GRID. Each row shows who it is for, what it costs and its status today. Check the official organiser before you register or share personal details.
          </Rise>
        </header>

        <section className="jn-wrap jn-dir" aria-labelledby="jn-dir-title">
          <h2 id="jn-dir-title" className="sr-only">Opportunity directory</h2>
          <div className="jn-dir__bar">
            <div className="jn-dir__filters" role="group" aria-label="Filter opportunities">
              {FILTERS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={filter === key}
                  className={`jn-pill ${filter === key ? "is-on" : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {filter === key && (
                    <motion.span className="jn-pill__fill" layoutId="jn-dir-fill" transition={{ duration: reduce ? 0 : 0.45, ease: EASE_OUT }} />
                  )}
                  <span className="jn-pill__label">{label} <small>{count(key)}</small></span>
                </button>
              ))}
            </div>
            <p className="jn-small" aria-live="polite">Showing {rows.length} of {ROWS.length}</p>
          </div>

          <div className="jn-dir__head" aria-hidden="true">
            <span>Opportunity</span><span>Who it is for</span><span>Cost</span><span>Status</span>
          </div>
          <motion.ul className="jn-dir__list" layout={!reduce}>
            <AnimatePresence mode="popLayout" initial={false}>
              {rows.map((row) => (
                <motion.li
                  key={row.id}
                  layout={!reduce}
                  className="jn-dir__row"
                  initial={reduce ? false : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, transition: { duration: 0.18 } }}
                  transition={{ duration: 0.55, ease: EASE_OUT }}
                >
                  <RowLink row={row}>
                    <span className="jn-dir__name">
                      <strong>{row.title}</strong>
                      <small>{row.by}</small>
                      <span>{row.note}</span>
                    </span>
                    <span className="jn-dir__cell" data-label="Who it is for">{row.eligibility}</span>
                    <span className="jn-dir__cell" data-label="Cost">{row.cost}</span>
                    <span className="jn-dir__cell" data-label="Status">
                      <span className={`jn-status jn-status--${row.status[0]}`}>{row.status[1]}</span>
                    </span>
                    <span className="jn-dir__go" aria-hidden="true"><ArrowUpRight weight="bold" /></span>
                    {row.href && <span className="sr-only">(opens the official site in a new tab)</span>}
                  </RowLink>
                </motion.li>
              ))}
            </AnimatePresence>
          </motion.ul>
        </section>

        <section className="jn-wrap jn-op-rule" aria-labelledby="jn-op-rule-title">
          <div>
            <h2 id="jn-op-rule-title" className="jn-h3">No invented calendar.</h2>
            <p>Only confirmed opportunities get a date. A past programme page is a record of an event, not an open call. Participation in a future programme depends on the terms and eligibility published for it.</p>
          </div>
          <Link className="gh-btn gh-btn--ghost" to="/programmes/upcoming">
            <span className="roll" data-label="Upcoming events"><span>Upcoming events</span></span>
          </Link>
        </section>
      </main>
    </PageFrame>
  );
}
